import { AlertDTO, CreateAlertDTO } from '@meshwatch/types';

import { DynamoDBClient } from '../../shared';
import { DEFAULT_DYNAMO_DB_CLIENT } from '../../shared/aws/dynamodb';
import { ForbiddenException, validationBoom, NotFoundException } from '../../shared/rest';
import BaseService, { ServiceResponse } from '../../shared/services';
import Authorization from '../../shared/services/access';
import { FormErrorsBoom } from '../../shared/validation/types';
import { DynamoMonitorDatasource, MonitorDatasource } from '../monitors/db';

import { AlertDatasource, DynamoAlertDatasource } from './db';
import { validateCreateAlertPayload } from './validation';

export const ALERT_ANY_MONITOR_SCHEDULER = 'alert-any-monitor';

export class AlertService extends BaseService {
  private alertDatasource: AlertDatasource;
  private monitorDatasource: MonitorDatasource;

  public constructor(dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT) {
    super();
    this.alertDatasource = new DynamoAlertDatasource(dynamoClient);
    this.monitorDatasource = new DynamoMonitorDatasource(dynamoClient);
  }

  public listAlerts = async (
    userId: string
  ): Promise<ServiceResponse<AlertDTO[] | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const alerts = await this.alertDatasource.listAlerts(userId);
      return this.serviceResponse(alerts);
    });
  };

  public deleteAlert = async (userId: string, alertId: string) => {
    return this.tryExecute(async () => {
      const existingAlert = await this.getAlertById(userId, alertId);
      await this.alertDatasource.deleteAlert(userId, existingAlert.monitorId, alertId);
      return this.serviceResponse(existingAlert);
    });
  };

  public updateAlert = async (userId: string, alertId: string, payload: CreateAlertDTO) => {
    const validationResponse = await validateCreateAlertPayload(payload);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }
    return this.tryExecute(async () => {
      const existingAlert = await this.getAlertById(userId, alertId);
      if (Authorization.of(userId).cannotAccessEntity(existingAlert)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      const scheduler = await this.getSchedulerByMonitorIdentifier(userId, payload.monitorId);

      const alert = await this.alertDatasource.updateAlert({
        ...payload,
        scheduler,
        id: alertId,
        created: existingAlert.created,
      });

      return { statusCode: 200, body: alert };
    });
  };

  public createAlert = async (
    userId: string,
    payload: CreateAlertDTO
  ): Promise<ServiceResponse<AlertDTO | FormErrorsBoom<CreateAlertDTO>>> => {
    const validationResponse = await validateCreateAlertPayload(payload);
    if (validationResponse.error) {
      return this.serviceResponseFromBoom(validationBoom(validationResponse.errors));
    }

    return this.tryExecute(async () => {
      if (Authorization.of(userId).cannotAccessEntity(payload)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      const scheduler = await this.getSchedulerByMonitorIdentifier(userId, payload.monitorId);
      const alert = await this.alertDatasource.createAlert({
        ...payload,
        scheduler,
      });

      return { statusCode: 201, body: alert };
    });
  };

  private getAlertById = async (userId: string, alertId: string): Promise<AlertDTO> => {
    const userAlerts = await this.alertDatasource.listAlerts(userId);
    const alert = userAlerts.find(alert => alert.id === alertId);
    if (!alert) {
      throw new NotFoundException(`Could not find alert id = ${alertId}`);
    }
    return alert;
  };

  private getSchedulerByMonitorIdentifier = async (
    userId: string,
    monitorId: string
  ): Promise<string> => {
    let scheduler = ALERT_ANY_MONITOR_SCHEDULER;
    if (monitorId !== ALERT_ANY_MONITOR_SCHEDULER) {
      const monitor = await this.monitorDatasource.getMonitor(userId, monitorId);
      scheduler = monitor.scheduler;
    }
    return scheduler;
  };
}

const alertService = new AlertService();

export default alertService;
